import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { CONTENT_MAX_WIDTH } from '../../config'

type UnitsPageShellProps = {
  topbarHeight: number
}

type UnitsPageScrollAreaProps = {
  viewportHeight: number
}

type AvailabilityProps = {
  isavailable: 0 | 1
}

export const UnitsPageScrollArea = styled('main', {
  shouldForwardProp: (prop) => prop !== 'viewportHeight',
})<UnitsPageScrollAreaProps>(({ theme, viewportHeight }) => ({
  width: '100%',
  height: viewportHeight,
  overflow: 'auto',
  padding: theme.spacing(2),
  boxSizing: 'border-box',
}))

export const UnitsPageShell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'topbarHeight',
})<UnitsPageShellProps>(({ theme, topbarHeight }) => ({
  width: '100%',
  maxWidth: CONTENT_MAX_WIDTH,
  margin: '0 auto',
  marginTop: topbarHeight,
  paddingBottom: theme.spacing(4),
}))

export const UnitsHeroSection = styled('section')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid rgba(173, 223, 255, 0.1)',
  background:
    'radial-gradient(circle at top left, rgba(67, 145, 190, 0.26) 0%, rgba(12, 18, 27, 0) 34%), linear-gradient(180deg, rgba(14, 19, 25, 0.98) 0%, rgba(8, 12, 18, 1) 100%)',
  boxShadow: '0 22px 48px rgba(0, 0, 0, 0.22)',
  marginBottom: theme.spacing(4),
}))

export const UnitsHeroOverlay = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(120deg, rgba(12, 17, 24, 0.05) 0%, rgba(12, 17, 24, 0.4) 45%, rgba(12, 17, 24, 0.88) 100%)',
  pointerEvents: 'none',
}))

export const UnitsHeroGrid = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'stretch',
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'minmax(0, 1.1fr) minmax(320px, 0.9fr)',
  },
}))

export const UnitsHeroTextColumn = styled('div')(() => ({
  minWidth: 0,
}))

export const UnitsHeroBody = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4.5),
  },
}))

export const UnitsHeroEyebrow = styled(Typography)(() => ({
  color: 'rgba(180, 226, 255, 0.94)',
  fontSize: '0.74rem',
  fontWeight: 800,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
}))

export const UnitsHeroTitle = styled(Typography)(({ theme }) => ({
  color: '#f6fbff',
  fontSize: '2rem',
  fontWeight: 800,
  lineHeight: 1.02,
  maxWidth: 640,
  [theme.breakpoints.up('sm')]: {
    fontSize: '2.4rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.85rem',
  },
}))

export const UnitsHeroDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(230, 240, 250, 0.8)',
  fontSize: '0.98rem',
  lineHeight: 1.65,
  maxWidth: 620,
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.05rem',
  },
}))

export const UnitsHeroStats = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}))

export const UnitsHeroStatCard = styled('div')(({ theme }) => ({
  minWidth: '100%',
  flex: '1 1 180px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.2),
  padding: theme.spacing(1.2, 1.4),
  border: '1px solid rgba(173, 223, 255, 0.12)',
  backgroundColor: 'rgba(15, 22, 31, 0.6)',
  backdropFilter: 'blur(4px)',
  [theme.breakpoints.up('sm')]: {
    minWidth: 180,
  },
}))

export const UnitsHeroStatIcon = styled('div')(() => ({
  width: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  color: '#9edaff',
  backgroundColor: 'rgba(37, 74, 103, 0.34)',
  flexShrink: 0,
}))

export const UnitsHeroStatLabel = styled(Typography)(() => ({
  color: 'rgba(180, 226, 255, 0.78)',
  fontSize: '0.7rem',
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
}))

export const UnitsHeroStatValue = styled(Typography)(() => ({
  color: '#f6fbff',
  fontSize: '0.96rem',
  fontWeight: 600,
}))

export const UnitsHeroActionGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.25),
  paddingTop: theme.spacing(0.5),
}))

export const UnitsHeroPrimaryButton = styled(Button)(({ theme }) => ({
  minWidth: 180,
  width: '100%',
  textTransform: 'none',
  fontWeight: 700,
  paddingInline: theme.spacing(2.2),
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}))

export const UnitsHeroSecondaryButton = styled(Button)(({ theme }) => ({
  minWidth: 200,
  width: '100%',
  textTransform: 'none',
  fontWeight: 700,
  paddingInline: theme.spacing(2.2),
  color: 'rgba(232, 244, 255, 0.92)',
  borderColor: 'rgba(173, 223, 255, 0.24)',
  backgroundColor: 'rgba(16, 23, 32, 0.4)',
  '&:hover': {
    borderColor: 'rgba(173, 223, 255, 0.42)',
    backgroundColor: 'rgba(16, 23, 32, 0.62)',
  },
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}))

export const UnitsHeroGraphicColumn = styled('div')(({ theme }) => ({
  minWidth: 0,
  display: 'flex',
  borderTop: '1px solid rgba(173, 223, 255, 0.08)',
  background:
    'linear-gradient(180deg, rgba(10, 15, 22, 0.42) 0%, rgba(10, 15, 22, 0.12) 100%)',
  [theme.breakpoints.up('lg')]: {
    borderTop: 'none',
    borderLeft: '1px solid rgba(173, 223, 255, 0.08)',
  },
}))

export const UnitsHeroGraphicFrame = styled('div')(({ theme }) => ({
  width: '100%',
  marginLeft: 'auto',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 520,
  },
}))

export const UnitsSectionIntro = styled('section')(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}))

export const UnitsSectionEyebrow = styled(Typography)(({ theme }) => ({
  color: 'rgba(180, 226, 255, 0.9)',
  fontSize: '0.74rem',
  fontWeight: 800,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  marginBottom: theme.spacing(0.75),
}))

export const UnitsSectionTitle = styled(Typography)(({ theme }) => ({
  color: '#f6fbff',
  fontSize: '1.5rem',
  fontWeight: 700,
  lineHeight: 1.1,
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.8rem',
  },
}))

export const UnitsListingGrid = styled('section')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2.5),
  },
  [theme.breakpoints.up('xl')]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
}))

export const UnitsCardRoot = styled('article')(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid rgba(173, 223, 255, 0.08)',
  background:
    'linear-gradient(180deg, rgba(14, 18, 24, 0.96) 0%, rgba(11, 15, 20, 1) 100%)',
  boxShadow: '0 18px 36px rgba(0, 0, 0, 0.2)',
  transition: 'transform 180ms ease, border-color 180ms ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: 'rgba(120, 216, 255, 0.28)',
  },
}))

export const UnitsCardMedia = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1.5),
  paddingBottom: 0,
}))

export const UnitsCardImageFrame = styled('div')(() => ({
  height: 230,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  border: '1px solid rgba(173, 223, 255, 0.08)',
  background:
    'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.16) 0%, rgba(24,34,48,0.18) 42%, rgba(9,13,19,0.82) 100%)',
}))

export const UnitsCardImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: theme.spacing(1.5),
}))

export const UnitsCardTagList = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 12,
  left: 12,
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
}))

export const UnitsCardTag = styled('span')(({ theme }) => ({
  padding: theme.spacing(0.45, 1),
  border: '1px solid rgba(173, 223, 255, 0.14)',
  backgroundColor: 'rgba(10, 16, 24, 0.78)',
  color: '#d8f1ff',
  fontSize: '0.66rem',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}))

export const UnitsCardBody = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.15),
  padding: theme.spacing(2),
}))

export const UnitsCardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
}))

export const UnitsCardTitleGroup = styled('div')(() => ({
  minWidth: 0,
}))

export const UnitsCardTitle = styled(Typography)(() => ({
  color: '#f6fbff',
  fontSize: '1.35rem',
  fontWeight: 700,
  lineHeight: 1,
}))

export const UnitsCardAvailability = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isavailable',
})<AvailabilityProps>(({ isavailable }) => ({
  marginTop: 4,
  color: isavailable
    ? 'rgba(255, 130, 103, 0.95)'
    : 'rgba(230, 240, 250, 0.56)',
  fontSize: '0.78rem',
  fontWeight: 800,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
}))

export const UnitsCardPrice = styled('div')(({ theme }) => ({
  alignSelf: 'flex-start',
  flexShrink: 0,
  padding: theme.spacing(0.8, 1.1),
  border: '1px solid rgba(173, 223, 255, 0.12)',
  backgroundColor: 'rgba(15, 22, 31, 0.56)',
  textAlign: 'right',
}))

export const UnitsCardPriceLabel = styled(Typography)(() => ({
  color: 'rgba(180, 226, 255, 0.7)',
  fontSize: '0.66rem',
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
}))

export const UnitsCardPriceValue = styled(Typography)(() => ({
  color: '#ff845f',
  fontSize: '1.1rem',
  fontWeight: 800,
}))

export const UnitsCardDescription = styled(Typography)(() => ({
  color: 'rgba(230, 240, 250, 0.78)',
  fontSize: '0.98rem',
  lineHeight: 1.55,
  minHeight: 72,
}))

export const UnitsCardActionButton = styled(Button)(({ theme }) => ({
  marginTop: 'auto',
  alignSelf: 'stretch',
  textTransform: 'none',
  fontWeight: 700,
  color: 'rgba(232, 244, 255, 0.92)',
  borderColor: 'rgba(173, 223, 255, 0.24)',
  backgroundColor: 'rgba(16, 23, 32, 0.4)',
  '&:hover': {
    borderColor: 'rgba(173, 223, 255, 0.42)',
    backgroundColor: 'rgba(16, 23, 32, 0.62)',
  },
  '&.MuiButton-root': {
    paddingBlock: theme.spacing(1.1),
  },
}))
